import * as z from 'zod';

/**
 * Validate a US National Provider Identifier (NPI).
 *
 * NPI is a 10-digit number issued by CMS. It includes a check digit computed via
 * the Luhn algorithm with a fixed prefix `80840` (the ISO/IEC 7812 issuer prefix
 * for NPIs). Validation:
 *   1. Must be exactly 10 digits.
 *   2. Prepend `80840` → 15 digits total.
 *   3. Apply standard Luhn: from the right, double every second digit, subtracting
 *      9 from any doubled value ≥ 10. Sum all digits.
 *   4. The NPI is valid iff the sum is divisible by 10.
 *
 * See: https://www.cms.gov/Regulations-and-Guidance/Administrative-Simplification/NationalProvIdentStand/Downloads/NPIcheckdigit.pdf
 */
export const isValidNpi = (value: string): boolean => {
  if (!/^\d{10}$/.test(value)) {
    return false;
  }

  const digits = `80840${value}`.split('').map((d) => Number(d));

  // Standard Luhn: from the right, double every second digit (1, 3, 5, ...).
  let sum = 0;
  for (let i = digits.length - 1; i >= 0; i--) {
    const fromRight = digits.length - 1 - i;
    let n = digits[i]!;
    if (fromRight % 2 === 1) {
      n *= 2;
      if (n >= 10) n -= 9;
    }
    sum += n;
  }

  return sum % 10 === 0;
};

/** Strict zod schema for an NPI value. Rejects anything that isn't a Luhn-valid 10-digit NPI. */
export const npiSchema = z.string().refine(isValidNpi, { message: 'Invalid NPI' });

/**
 * Form-friendly zod schema for an optional NPI field. Accepts an empty string (field cleared)
 * or a Luhn-valid 10-digit NPI; rejects anything else with `'Invalid NPI'`.
 */
export const npiFieldSchema = z.string().refine((v) => v === '' || isValidNpi(v), { message: 'Invalid NPI' });
